import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Form } from "./FormService";

// GraphQL queries
const SUBMISSION_STAT_LIST_QUERY = gql`
  query SubmissionStatList(
    $filter: SubmissionStatFilter
    $params: PaginationAndSorting
  ) {
    submissionStatList(filter: $filter, params: $params) {
      code
      data {
        id
        formId
        year
        month
        totalSubmissionsCnt
        submissionsCnt
        filesCnt
        filesSize
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_STAT_GET_QUERY = gql`
  query SubmissionStatGet($id: ID) {
    submissionStatGet(id: $id) {
      code
      data {
        id
        formId
        year
        month
        totalSubmissionsCnt
        submissionsCnt
        filesCnt
        filesSize
        createdAt
        updatedAt
        form {
          id
          name
          path
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBMISSION_STAT_GET_MANY_QUERY = gql`
  query SubmissionStatGetMany($ids: [ID]) {
    submissionStatGetMany(ids: $ids) {
      code
      data {
        id
        formId
        year
        month
        totalSubmissionsCnt
        submissionsCnt
        filesCnt
        filesSize
        createdAt
        updatedAt
        form {
          id
          name
          path
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface SubmissionStat {
  id?: string | undefined;
  formId?: string | undefined;
  year?: number | undefined;
  month?: number | undefined;
  totalSubmissionsCnt?: number | undefined;
  submissionsCnt?: number | undefined;
  filesCnt?: number | undefined;
  filesSize?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  form?: Form;
}

export interface SubmissionStatFilter {
  id?: string[] | null | undefined;
  formId?: string[] | null | undefined;
  year?: number[] | null | undefined;
  month?: number[] | null | undefined;
}

/**
 * Submission Statistics service that handles all submission stat-related API calls
 * Supports read operations: list, get, getMany
 */
export class SubmissionStatService {
  /**
   * Get a list of submission statistics with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: SubmissionStatFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(SUBMISSION_STAT_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single submission statistic by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(SUBMISSION_STAT_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple submission statistics by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      SUBMISSION_STAT_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }
}
